import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import VideoPlayer from "@/components/VideoPlayer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ChevronRight, CheckCircle2, GraduationCap, ExternalLink, Clock } from "lucide-react";
import MaterialsManager from "@/components/MaterialsManager";

export default function CoursePlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [moduleIdx, setModuleIdx] = useState(0);
  const [enrollment, setEnrollment] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const fetchAll = async () => {
    const { data } = await api.get(`/courses/${id}`);
    setCourse(data);
    try {
      const { data: enr } = await api.post(`/courses/${id}/enroll`);
      setEnrollment(enr);
    } catch {}
  };

  useEffect(() => { fetchAll(); }, [id]);

  const startEvaluation = async () => {
    const { data } = await api.get(`/courses/${id}/evaluation`);
    setEvaluation(data);
    setAnswers({});
    setResult(null);
  };

  const submitEvaluation = async () => {
    const arr = (course.evaluation?.questions || []).map((_, i) => answers[i] ?? -1);
    if (arr.some((v) => v < 0)) { toast.error("Responda todas as questões"); return; }
    try {
      const { data } = await api.post(`/courses/${id}/evaluation/submit`, { answers: arr });
      setResult(data);
      if (data.passed) toast.success(`Aprovado! Nota ${data.score}`);
      else toast.error(`Não atingiu a nota mínima. ${data.score}%`);
    } catch (e) { toast.error("Erro ao submeter"); }
  };

  if (!course) return <div className="text-slate-500">Carregando…</div>;

  const modules = course.modules || [];
  const current = modules[moduleIdx];
  const mp = enrollment?.modules_progress || {};
  const isLive = course.type === "online_live";

  return (
    <div className="space-y-6" data-testid="course-player-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Badge variant="outline" className="mb-2">{isLive ? "Ao vivo" : "Vídeo"}</Badge>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold">{course.title}</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">{course.description}</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">Progresso</div>
          <div className="font-display font-bold text-lg">{enrollment?.progress || 0}%</div>
          <Progress value={enrollment?.progress || 0} className="w-40 mt-1" />
        </div>
      </header>

      {isLive ? (
        <Card><CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-3 text-slate-700">
            <Clock className="w-4 h-4 text-[var(--unigr-red)]" />
            <span><strong>Plataforma:</strong> {course.platform || "—"}</span>
          </div>
          <div className="flex items-center gap-3 text-slate-700">
            <GraduationCap className="w-4 h-4 text-[var(--unigr-red)]" />
            <span><strong>Instrutor:</strong> {course.instructor || "—"}</span>
          </div>
          <div className="flex items-center gap-3 text-slate-700">
            <Clock className="w-4 h-4 text-[var(--unigr-red)]" />
            <span><strong>Data/Hora:</strong> {course.scheduled_at || "A definir"}</span>
          </div>
          {course.meeting_url && (
            <a href={course.meeting_url} target="_blank" rel="noopener noreferrer">
              <Button className="btn-red mt-2" data-testid="live-join-btn"><ExternalLink className="w-4 h-4 mr-2" />Acessar sessão ao vivo</Button>
            </a>
          )}
        </CardContent></Card>
      ) : (
        <>
          {current && (
            <>
              <div className="grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-8">
                  <VideoPlayer
                    courseId={id}
                    module={current}
                    moduleIndex={moduleIdx}
                    onComplete={() => { fetchAll(); }}
                  />
                  <div className="mt-4 flex items-center justify-between">
                    <div>
                      <div className="text-xs text-slate-500 uppercase tracking-wide">Módulo {moduleIdx + 1} de {modules.length}</div>
                      <div className="font-display font-bold text-lg">{current.title}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" disabled={moduleIdx === 0} onClick={() => setModuleIdx(moduleIdx - 1)} data-testid="prev-module-btn">Anterior</Button>
                      <Button className="btn-navy" disabled={moduleIdx === modules.length - 1} onClick={() => setModuleIdx(moduleIdx + 1)} data-testid="next-module-btn">
                        Próximo <ChevronRight className="ml-1 w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Materiais complementares do módulo atual */}
                  {(current.materials && current.materials.length > 0) && (
                    <Card className="mt-4"><CardContent className="p-5">
                      <MaterialsManager
                        courseId={id}
                        moduleIndex={moduleIdx}
                        materials={current.materials || []}
                        readOnly
                      />
                    </CardContent></Card>
                  )}
                </div>
                <div className="lg:col-span-4">
                  <Card><CardContent className="p-5">
                    <div className="font-display font-bold mb-3">Módulos</div>
                    <ul className="space-y-1">
                      {modules.map((m, i) => {
                        const done = mp[String(i)]?.completed;
                        return (
                          <li key={i}>
                            <button
                              onClick={() => setModuleIdx(i)}
                              data-testid={`module-${i}`}
                              className={"w-full text-left px-3 py-2 rounded-md border text-sm flex items-center justify-between transition-colors " +
                                (i === moduleIdx ? "border-[var(--unigr-navy)] bg-slate-50" : "border-transparent hover:bg-slate-50")}
                            >
                              <span>{i + 1}. {m.title}</span>
                              {done && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </CardContent></Card>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Evaluation */}
      {course.evaluation && (course.evaluation.questions || []).length > 0 && (
        <Card data-testid="evaluation-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="eyebrow">Avaliação final</div>
                <div className="font-display font-bold text-lg">Teste de aproveitamento</div>
                <div className="text-sm text-slate-600">Nota mínima: {course.evaluation.passing_score || 70}%</div>
              </div>
              <Button className="btn-red" onClick={startEvaluation} data-testid="start-evaluation-btn">Iniciar avaliação</Button>
            </div>

            {evaluation && (
              <div className="space-y-4 mt-4">
                {evaluation.questions.map((q, idx) => (
                  <div key={idx} className="p-4 rounded-md border border-[var(--unigr-border)]">
                    <div className="font-medium">{idx + 1}. {q.text}</div>
                    <div className="grid sm:grid-cols-2 gap-2 mt-3">
                      {q.options.map((op, oi) => (
                        <label key={oi} className={"px-3 py-2 rounded-md border cursor-pointer text-sm " + (answers[q.index] === oi ? "border-[var(--unigr-navy)] bg-slate-50" : "border-[var(--unigr-border)]")}>
                          <input
                            type="radio"
                            name={`q-${q.index}`}
                            className="mr-2"
                            checked={answers[q.index] === oi}
                            onChange={() => setAnswers((a) => ({ ...a, [q.index]: oi }))}
                            data-testid={`q-${idx}-option-${oi}`}
                          />
                          {op}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                <Button className="btn-navy" onClick={submitEvaluation} data-testid="submit-evaluation-btn">Enviar respostas</Button>
                {result && (
                  <div className={"mt-4 p-4 rounded-md " + (result.passed ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200")} data-testid="evaluation-result">
                    <div className="font-display font-bold text-lg">{result.passed ? "Aprovado!" : "Não atingiu a nota mínima"}</div>
                    <div className="text-sm text-slate-700">
                      Você acertou {result.correct} de {result.total} ({result.score}%)
                    </div>
                    {result.certificate && (
                      <div className="mt-3">
                        <Button className="btn-red" onClick={() => navigate(`/certificates`)} data-testid="view-cert-btn">Ver meus certificados</Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
